import { useState } from "react";
import { AlertTriangle, ExternalLink, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export function SourceCard({ source, cited, compact }) {
  const failed = source.status === "failed";
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      id={`source-${source.n}`}
      className="group flex min-w-0 flex-col rounded-xl border border-white/[0.08] bg-ink-900 p-3 transition-[border-color,background-color] duration-150 hover:border-white/[0.18] hover:bg-ink-850"
      data-testid={`research-source-${source.n}`}
      data-cited={cited ? "true" : "false"}
    >
      <div className="flex items-center gap-2 text-[11.5px] text-zinc-500">
        <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white/[0.08] px-1 font-mono text-[10.5px] text-zinc-200">{source.n}</span>
        <span className="min-w-0 truncate" data-testid="research-source-domain">{source.domain}</span>
        {source.authoritative && <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-300/80" aria-label="Official or primary source" />}
        <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className="mt-1.5 line-clamp-2 text-[13.5px] font-medium leading-snug text-zinc-100" data-testid="research-source-title">{source.title}</div>
      {!compact && source.snippet && <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-zinc-500">{source.snippet}</p>}
      {!compact && <div className="mt-1.5 truncate font-mono text-[10.5px] text-zinc-600" data-testid="research-source-url">{source.url}</div>}
      {failed && (
        <span className="mt-2 inline-flex items-center gap-1 self-start rounded-full border border-amber-300/20 px-2 py-0.5 text-[10.5px] text-amber-200/80" data-testid="research-source-failed">
          <AlertTriangle className="h-3 w-3" /> Full page couldn't be read — search excerpt used
        </span>
      )}
    </a>
  );
}

export function SourceList({ sources, cited, compact = false, initial = 6 }) {
  const [all, setAll] = useState(false);
  if (!sources?.length) return null;
  const shown = all ? sources : sources.slice(0, initial);
  return (
    <div data-testid="research-sources">
      <div className={cn("grid gap-2", compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3")}>
        {shown.map((s) => <SourceCard key={s.n} source={s} cited={cited?.has(s.n)} compact={compact} />)}
      </div>
      {sources.length > initial && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 text-[12.5px] text-zinc-400 underline-offset-4 hover:text-white hover:underline" data-testid="research-sources-toggle">
          {all ? "Show fewer sources" : `Show all ${sources.length} sources`}
        </button>
      )}
    </div>
  );
}
